import type { ReactNode } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import {
  HR_BASE_PATH,
  HR_NAV_ITEMS,
  type HrRequirement,
} from "../config/hr.nav";
import { hrEntryRequirements } from "../hooks/useHrNav";
import HrRouteGuard, { HrHomeRedirect } from "./HrRouteGuard";

const HrDashboardPage = lazyWithRetry(
  () => import("@/page/hr/HrDashboardPage"),
);
const HrAttendancePage = lazyWithRetry(
  () => import("@/page/hr/HrAttendancePage"),
);
const HrPayrollPage = lazyWithRetry(() => import("@/page/hr/HrPayrollPage"));
const HrPayslipPrintPage = lazyWithRetry(
  () => import("@/page/hr/HrPayslipPrintPage"),
);
const HrAnalyticsLayout = lazyWithRetry(() => import("@/page/hr/analytics/HrAnalyticsLayout"));
const AnalyticsOverviewPage = lazyWithRetry(() => import("@/page/hr/analytics/AnalyticsOverviewPage"));
const AnalyticsPerformancePage = lazyWithRetry(() => import("@/page/hr/analytics/AnalyticsPerformancePage"));
const AnalyticsPersonPage = lazyWithRetry(() => import("@/page/hr/analytics/AnalyticsPersonPage"));
const AnalyticsAttendancePage = lazyWithRetry(() => import("@/page/hr/analytics/AnalyticsAttendancePage"));
const AnalyticsLeavePage = lazyWithRetry(() => import("@/page/hr/analytics/AnalyticsLeavePage"));
const AnalyticsWorkforcePage = lazyWithRetry(() => import("@/page/hr/analytics/AnalyticsWorkforcePage"));
const AnalyticsPayrollPage = lazyWithRetry(() => import("@/page/hr/analytics/AnalyticsPayrollPage"));
const HrSettingsPage = lazyWithRetry(() => import("@/page/hr/HrSettingsPage"));
const HrPeoplePage = lazyWithRetry(() => import("@/page/hr/HrPeoplePage"));
const HrApprovalsPage = lazyWithRetry(
  () => import("@/page/hr/HrApprovalsPage"),
);
// HR's own Automation and Reports — deliberately NOT the CRM's `/automation`
// and `/report`. See the header of `config/hr.nav.ts`.
const HrAutomationLayout = lazyWithRetry(() => import("@/page/hr/automation/HrAutomationLayout"));
const MyAutomationsPage = lazyWithRetry(() => import("@/page/hr/automation/MyAutomationsPage"));
const AutomationTemplatesPage = lazyWithRetry(() => import("@/page/hr/automation/AutomationTemplatesPage"));
const BuiltInRulesPage = lazyWithRetry(() => import("@/page/hr/automation/BuiltInRulesPage"));
const AutomationLogPage = lazyWithRetry(() => import("@/page/hr/automation/AutomationLogPage"));
const AutomationBuilderPage = lazyWithRetry(() => import("@/page/hr/automation/AutomationBuilderPage"));
const HrReportsPage = lazyWithRetry(() => import("@/page/hr/HrReportsPage"));

/**
 * The capabilities a screen needs = those of its nav entry, so a user can
 * never reach a screen by URL that the sidebar would not offer them. A route
 * whose entry is missing from the manifest fails CLOSED (no requirement can
 * be met) rather than open.
 */
function requirementsFor(routeKey: string): readonly HrRequirement[] {
  const item = HR_NAV_ITEMS.find((i) => i.routeKey === routeKey);
  if (!item) {
    console.error(`[hrRoutes] no HR nav entry "${routeKey}" — route locked`);
    return [];
  }
  return hrEntryRequirements(item);
}

/**
 * Every HR screen goes through `HrRouteGuard` (capability gate + HR's scroll
 * container and body tint). Still only the CLIENT gate — `/suites/hr/*` is
 * gated server-side on the same capabilities, and that is the boundary that
 * actually protects data.
 *
 * Lazy-loaded so HR adds ~0 to the CRM's first paint for tenants without it.
 */
function page(routeKey: string, element: ReactNode) {
  return (
    <HrRouteGuard requires={requirementsFor(routeKey)}>{element}</HrRouteGuard>
  );
}

/**
 * HR Suite routes, mounted as children of AppLayout so the shell (header,
 * notifications, quick search, toasts) is shared. Paths are relative because
 * they nest under the layout's "/" route.
 */
export const hrRoutes: RouteObject[] = [
  {
    // `/hr` alone → the first screen this user can open.
    path: HR_BASE_PATH.slice(1),
    element: <HrHomeRedirect />,
  },
  {
    path: "hr/dashboard",
    element: page("hr-dashboard", <HrDashboardPage />),
  },
  {
    path: "hr/attendance",
    element: page("hr-attendance", <HrAttendancePage />),
  },
  // Leave and WFH are sub-tabs of Attendance, not screens of their own; their
  // manifest paths land on the matching tab (the page gates each tab).
  // Guarded like every other HR path: without the capability the redirect
  // never runs (the guard answers), instead of bouncing through Attendance.
  {
    path: "hr/leave",
    element: page("hr-leave", <Navigate to={`${HR_BASE_PATH}/attendance?tab=leave`} replace />),
  },
  {
    path: "hr/wfh",
    element: page("hr-wfh", <Navigate to={`${HR_BASE_PATH}/attendance?tab=wfh`} replace />),
  },
  {
    // Open to any HR user — the server lists only what the caller may decide.
    path: "hr/approvals",
    element: page("hr-approvals", <HrApprovalsPage />),
  },
  {
    // Specific path first. Same gate as Payroll; the server decides which
    // payslip this caller may open (own released, or payroll with amounts).
    path: "hr/payroll/payslip/:payslipId/print",
    element: page("hr-payroll", <HrPayslipPrintPage />),
  },
  {
    path: "hr/payroll",
    element: page("hr-payroll", <HrPayrollPage />),
  },
  {
    // Sub-routes render in the layout's <Outlet />, under its single guard.
    path: "hr/analytics",
    element: page("hr-analytics", <HrAnalyticsLayout />),
    children: [
      { index: true, element: <Navigate to="overview" replace /> },
      { path: "overview", element: <AnalyticsOverviewPage /> },
      { path: "performance", element: <AnalyticsPerformancePage /> },
      { path: "performance/:userId", element: <AnalyticsPersonPage /> },
      { path: "attendance", element: <AnalyticsAttendancePage /> },
      { path: "leave", element: <AnalyticsLeavePage /> },
      { path: "workforce", element: <AnalyticsWorkforcePage /> },
      { path: "payroll", element: <AnalyticsPayrollPage /> },
    ],
  },
  {
    // The builder is a full page of its own, so it sits outside the tabs.
    path: "hr/automation/new",
    element: page("hr-automation", <AutomationBuilderPage />),
  },
  {
    path: "hr/automation/edit/:automationId",
    element: page("hr-automation", <AutomationBuilderPage />),
  },
  {
    path: "hr/automation",
    element: page("hr-automation", <HrAutomationLayout />),
    children: [
      { index: true, element: <Navigate to="my" replace /> },
      { path: "my", element: <MyAutomationsPage /> },
      { path: "templates", element: <AutomationTemplatesPage /> },
      { path: "built-in", element: <BuiltInRulesPage /> },
      { path: "log", element: <AutomationLogPage /> },
    ],
  },
  {
    path: "hr/reports",
    element: page("hr-reports", <HrReportsPage />),
  },
  {
    path: "hr/settings",
    element: page("hr-settings", <HrSettingsPage />),
  },
  {
    path: "hr/people",
    element: page("hr-people", <HrPeoplePage />),
  },
  {
    // One employee — the same page renders the detail from the param.
    path: "hr/people/:employeeId",
    element: page("hr-people", <HrPeoplePage />),
  },
];
